import { TenantListContent } from '../CollectTenantList';
import { useEffect, useState } from 'react';
import { TenantRegistrationForm } from '../CollectTenantRegistrationForm';
import { useParams } from 'react-router';
import { getCollectAreaDetail, getWasteUnits } from 'apis/collects';
import { Tenant } from 'components/molecules/SelectTenantCard';
import { WasteUnitItem } from 'types/routeManagementTypes';
import { getRoutesCollectedTotalWeight } from 'apis/collect_route';
import { useCheckRouteCollectProcess } from 'util/hooks/useCheckRouteCollectProcess';
import { CollectingRouteSession } from 'util/collectingRoute';

type TenantRegistrationContentProps = {
  selectedTenant?: Tenant;
  setSelectedTenant?: (tenant: Tenant | undefined) => void;
  setHasCollectedSuccess?: (value: boolean) => void;
};

export const TenantRegistrationContent = ({
  selectedTenant,
  setSelectedTenant,
  setHasCollectedSuccess,
}: TenantRegistrationContentProps) => {
  const { siteId, routeId } = useParams() as {
    siteId: string;
    routeId: string;
  };

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [wasteUnitIds, setWasteUnitIds] = useState<number[]>([]);
  const [wasteUnits, setWasteUnits] = useState<WasteUnitItem[]>([]);
  const [showPage, setShowPage] = useState(false);
  const [siteName, setSiteName] = useState('');

  useCheckRouteCollectProcess();

  const initData = () => {
    setShowPage(false);

    getCollectAreaDetail({
      areaId: siteId,
      ...(routeId && { routeId: Number(routeId) }),
      successCallback: (data) => {
        setTenants(data.tenants || []);
        setSiteName(data.siteName);

        setWasteUnitIds(
          data.substituteRegistRelations.map((rel) => Number(rel.wasteUnitId))
        );
      },
      finallyCallback: () => {
        setShowPage(true);
      },
    });
  };

  useEffect(() => {
    initData();
  }, [siteId]);

  useEffect(() => {
    const fetchAllWasteUnits = async () => {
      const promises = wasteUnitIds.map(
        (id) =>
          new Promise<WasteUnitItem>((resolve) => {
            getWasteUnits({
              wasteUnitId: id,
              successCallback: (data) => resolve(data),
            });
          })
      );

      const units = await Promise.all(promises);
      setWasteUnits(units);
    };

    if (wasteUnitIds.length > 0) {
      setWasteUnits([]);
      fetchAllWasteUnits();
    }
  }, [wasteUnitIds]);

  const handleSelectTenant = (tenant: Tenant) => {
    setSelectedTenant?.(tenant);
  };

  if (!showPage) {
    return null;
  }

  if (!selectedTenant && tenants.length > 0) {
    return (
      <TenantListContent
        tenants={tenants}
        onTenantSelect={handleSelectTenant}
      />
    );
  }

  return (
    <TenantRegistrationForm
      wasteUnits={wasteUnits}
      tenant={selectedTenant}
      siteName={siteName}
      submitSuccess={() => {
        setSelectedTenant?.(undefined);
        CollectingRouteSession.addProcessedSite({
          routeId,
          siteId,
        });
        getRoutesCollectedTotalWeight();
        setHasCollectedSuccess?.(true);
      }}
    />
  );
};
